import { useDroppable } from '@dnd-kit/core';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import BattlefieldCard from './BattlefieldCard.js';

/**
 * Module-level ref so PlaytesterView can compute drop coordinates
 * (absolute position within the battlefield canvas) from drag events.
 */
let battlefieldEl: HTMLElement | null = null;
export function getBattlefieldEl(): HTMLElement | null {
  return battlefieldEl;
}

export default function PlayerBattlefield() {
  const battlefield = usePlaytesterStore((s) => s.battlefield);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'battlefield' });

  // Canvas height: at least enough to contain all cards, minimum 300px
  const minCanvasH = battlefield.reduce((max, inst) => {
    const bottom = (inst.y ?? 0) + 200;
    return Math.max(max, bottom);
  }, 300);

  // Combine the droppable ref with our module-level ref for coordinate lookup
  function setRef(el: HTMLDivElement | null) {
    battlefieldEl = el;
    setDropRef(el);
  }

  return (
    <div
      ref={setRef}
      // min-h-0 is required so this flex-1 item can shrink below its content's
      // intrinsic height when the bottom row grows at high zoom. Without it,
      // min-height:auto in a flex context prevents the battlefield from shrinking
      // and the bottom row is pushed off-screen.
      className={`flex-1 min-h-0 bg-gray-950 overflow-auto transition ${isOver ? 'bg-orange-950/20' : ''}`}
    >
      {/* Free-form canvas — cards are absolutely positioned */}
      <div
        className="relative w-full"
        style={{ minHeight: `${minCanvasH}px` }}
      >
        {battlefield.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] text-gray-700 italic">Drag cards here</span>
          </div>
        )}
        {battlefield.map((instance) => (
          <BattlefieldCard key={instance.instanceId} instance={instance} />
        ))}
      </div>
    </div>
  );
}
